class ConstructorAutocomplete extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allInstances = document.querySelectorAll('constructor-autocomplete');
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.pageBody = document.getElementsByTagName('body')[0];
    this.statusElement = this.querySelector('.predictive-search-status');
    this.loadingText = this.getAttribute('data-loading-text');
    this._mobileTabsInitialised = false;
    this._debounceTimer = null;

    this._setupAutocompleteListeners();
    window.SearchPopup?.mountPanels(this.querySelector('[data-search-panels]'));
    window.SearchPopup?.bindMobileTabClicks?.(this);
  }

  _debounce(fn, delay) {
    return (...args) => {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  _setupAutocompleteListeners() {
    this.input.form.addEventListener('submit', this._onFormSubmit.bind(this));
    this.input.addEventListener('focus', this._onFocus.bind(this));
    this.addEventListener('focusout', this._onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      clearTimeout(this._debounceTimer);
      this.abortController.abort();
      this.abortController = new AbortController();
      this.showIdle();
      return;
    }

    // SearchForm already debounces input; only add Constructor's extra delay.
    const debounceMs = window.constructorStrings?.autocompleteDebounceMs ?? 300;
    clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => {
      this.abortController.abort();
      this.abortController = new AbortController();
      this._getAutocompleteResults(this.searchTerm);
    }, debounceMs);
  }

  _onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a, [aria-selected="true"] [data-search-phrase]')) {
      event.preventDefault();
    } else {
      this.saveLastSearched();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.showIdle();
    }
  }

  _onFocus() {
    this.closest('search-modal')?.open?.();
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) {
      this.showIdle();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('data-search-state') === 'empty') {
      // Keep empty-state popup; do not reopen the results panel.
      return;
    } else if (this.hasAttribute('results')) {
      this.open();
    } else {
      this._getAutocompleteResults(this.searchTerm);
    }
  }

  _onFocusOut(event) {
    const relatedTarget = event?.relatedTarget || null;
    setTimeout(() => {
      const active = document.activeElement;
      // iOS keyboard "Done" blurs the input with no next focus target — keep popup open.
      if (!relatedTarget && (!active || active === document.body || active === document.documentElement)) {
        return;
      }

      // Keep the popup open while focus remains inside the search modal shell.
      const modal = this.closest('search-modal');
      if (modal) {
        if (modal.contains(active)) return;
        modal.close?.(false);
        return;
      }
      if (!this.contains(active)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.showIdle();
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this._switchOption('up');
        break;
      case 'ArrowDown':
        this._switchOption('down');
        break;
      case 'Enter':
        this._selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  _switchOption(direction) {
    const searchState = this.getAttribute('data-search-state');
    const hasResultsOpen =
      searchState === 'results' || (!searchState && (this.hasAttribute('open') || this.hasAttribute('results')));
    if (!hasResultsOpen) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item, button.button')).filter(
      (element) => element.offsetParent !== null
    );

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;
    while (selectedElementIndex === -1 && i < allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) selectedElementIndex = i;
      i++;
    }

    if (this.statusElement) this.statusElement.textContent = '';

    let activeElementIndex = 0;
    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    activeElement.setAttribute('aria-selected', 'true');
    if (selectedElement) selectedElement.setAttribute('aria-selected', 'false');
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  _selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] [data-search-phrase], button[aria-selected="true"]'
    );
    if (selectedOption) selectedOption.click();
  }

  _setLoadingState() {
    if (this.statusElement) {
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = this.loadingText;
      setTimeout(() => this.statusElement.setAttribute('aria-hidden', 'true'), 1000);
    }
    if (typeof window.SearchPopup?.showLoadingSkeleton === 'function') {
      window.SearchPopup.showLoadingSkeleton(this);
    } else {
      this.setAttribute('loading', '');
      window.SearchPopup?.setSearchState(this, 'results');
    }
  }

  /**
   * Debug surface for autocomplete (inspect in DevTools):
   *   window.__constructorAutocomplete
   * Enabled when theme setting constructor_debug_search_enabled is on,
   * or when window.__constructorAutocompleteDebug === true.
   */
  _debugSet(partial) {
    const enabled =
      window.__constructorAutocompleteDebug === true ||
      Boolean(window.Shopify?.theme?.settings?.constructor_debug_search_enabled) ||
      document.documentElement?.dataset?.constructorDebugSearch === 'true';
    if (!enabled) return;

    window.__constructorAutocomplete = {
      ...(window.__constructorAutocomplete || {}),
      ...partial,
      at: new Date().toISOString(),
    };
  }

  async _getAutocompleteResults(searchTerm) {
    if (!window.constructorio?.autocomplete?.getAutocompleteResults) return;
    if (searchTerm !== this.getQuery()) return;

    // Capture this request's signal — `this.abortController` is replaced on newer queries.
    const { signal } = this.abortController;
    const isStale = () => signal.aborted || searchTerm !== this.getQuery();

    // Cache per locale/country — same context keys as constructor-product-properties.js
    const LANG = (window.Shopify?.locale || 'EN').split('-')[0].toUpperCase();
    const COUNTRY = window.Shopify?.country || 'SK';
    const queryKey = `${LANG}:${COUNTRY}:${searchTerm.replace(/\s+/g, '-').toLowerCase()}`;

    this._setLoadingState();

    if (this.cachedResults[queryKey]) {
      this._debugSet({
        phase: 'cache-hit',
        searchTerm,
        queryKey,
        result: this.cachedResults[queryKey],
        localizedCollections: this.cachedResults[queryKey]._localizedCollections,
      });
      this._renderResults(this.cachedResults[queryKey]);
      return;
    }

    try {
      const productsLimit = window.constructorStrings?.autocompleteProductsLimit || 8;
      const suggestionsLimit = window.constructorStrings?.autocompleteSuggestionsLimit || 5;
      const categoriesLimit = window.constructorStrings?.autocompleteCategoriesLimit || 6;

      const result = await window.constructorio.autocomplete.getAutocompleteResults(searchTerm, {
        resultsPerSection: {
          Products: productsLimit,
          'Search Suggestions': suggestionsLimit,
          Categories: categoriesLimit,
        },
      });

      if (isStale()) return;

      const sections = result?.sections ?? {};
      const productHandles = (sections.Products ?? [])
        .map((item) => item.data?.id || item.data?.handle)
        .filter(Boolean)
        .map((h) => encodeURIComponent(h));
      const categoryHandles = (sections.Categories ?? [])
        .map((item) => item.data?.handle)
        .filter(Boolean);

      // Products → Shopify list-products view (current storefront context)
      // Categories → Storefront API @inContext (same as constructor-product-properties.js)
      const [productItemsHtml, localizedCollections] = await Promise.all([
        productHandles.length ? this._fetchProductsHtml(productHandles, signal) : '',
        categoryHandles.length ? this._fetchCollectionTitles(categoryHandles, signal) : {},
      ]);

      if (isStale()) return;

      const resultWithHtml = {
        ...result,
        _productItemsHtml: productItemsHtml,
        _localizedCollections: localizedCollections,
      };

      this._debugSet({
        phase: 'ready',
        searchTerm,
        queryKey,
        lang: LANG,
        country: COUNTRY,
        shopifyLocale: window.Shopify?.locale,
        shopifyCountry: window.Shopify?.country,
        response: result,
        sections,
        products: sections.Products || [],
        suggestions: sections['Search Suggestions'] || [],
        categories: sections.Categories || [],
        localizedCollections,
      });

      this.allInstances.forEach((instance) => {
        instance.cachedResults[queryKey] = resultWithHtml;
      });

      this._renderResults(resultWithHtml);

      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.searchQuery, {
          query: searchTerm,
          search_context: 'overlay',
          engine: 'constructor',
          products_count: sections.Products?.length || 0,
          collections_count: sections.Categories?.length || 0,
          result_product_ids: productHandles.map(decodeURIComponent),
        });
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('[ConstructorAutocomplete] Failed to fetch autocomplete results:', error);
      window.reportFrontendError?.({
        source: 'constructor',
        surface: 'ConstructorAutocomplete',
        message: error?.message || String(error),
        stack: error?.stack || '',
        error_type: 'api',
        context: { searchTerm: this.searchTerm },
      });
      this.close();
    }
  }

  async _fetchProductsHtml(handles, signal = this.abortController.signal) {
    const baseUrl = window.routes?.all_products_collection_url || '/collections/all';
    const BATCH_SIZE = 20;
    const batches = [];
    for (let i = 0; i < handles.length; i += BATCH_SIZE) {
      batches.push(handles.slice(i, i + BATCH_SIZE));
    }

    const campaign_code = new URLSearchParams(window.location.search).get('code');

    const htmlParts = await Promise.all(
      batches.map((batch) => {
        const url =
          `${baseUrl}?view=list-products&limit=${batch.length}&type=${batch.join('+')}` +
          (campaign_code ? `&q=${campaign_code}` : '');
        return fetch(url, { signal }).then((r) => {
          if (!r.ok) throw new Error(`list-products responded with ${r.status}`);
          return r.text();
        });
      })
    );

    return htmlParts.join('');
  }

  /**
   * Localized collection titles via Storefront API — mirrors constructor-product-properties.js:
   *   token:  window.themeSettings.custom_app_token
   *   domain: window.shopDomain
   *   lang:   window.Shopify.locale
   *   country: window.Shopify.country
   *   @inContext(language, country)
   *
   * @returns {Promise<Record<string, { title: string, url: string, isMatch: boolean }>>}
   */
  async _fetchCollectionTitles(handles, signal = this.abortController.signal) {
    const uniqueHandles = [...new Set((handles || []).filter(Boolean))];
    if (!uniqueHandles.length) return {};

    const token = window.themeSettings?.custom_app_token;
    const domain = window.shopDomain;
    if (!token || !domain) {
      this._debugSet({
        phase: 'collections-skip',
        reason: 'missing token or shopDomain',
        hasToken: Boolean(token),
        hasDomain: Boolean(domain),
      });
      return {};
    }

    const STOREFRONT_API_VERSION = '2025-10';
    const LANG = (window.Shopify?.locale || 'EN').split('-')[0].toUpperCase();
    const COUNTRY = window.Shopify?.country || 'SK';
    const endpoint = `https://${domain}/api/${STOREFRONT_API_VERSION}/graphql.json`;
    const collectionsBase = (window.routes?.collections_url || '/collections').replace(/\/$/, '');

    const variables = { lang: LANG, country: COUNTRY };
    const fields = uniqueHandles
      .map((handle, i) => {
        variables[`handle${i}`] = handle;
        return `c${i}: collection(handle: $handle${i}) {
          title
          handle
          collectionType: metafield(namespace: "custom", key: "collection_type") { value }
        }`;
      })
      .join('\n');

    const varDefs = [
      '$lang: LanguageCode!',
      '$country: CountryCode!',
      ...uniqueHandles.map((_, i) => `$handle${i}: String!`),
    ].join(', ');

    const query = `
      query GetAutocompleteCollectionTitles(${varDefs})
      @inContext(language: $lang, country: $country) {
        ${fields}
      }
    `;

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({ query, variables }),
        signal,
      });

      if (!res.ok) {
        this._debugSet({
          phase: 'collections-http-error',
          status: res.status,
          lang: LANG,
          country: COUNTRY,
          endpoint,
        });
        return {};
      }

      const json = await res.json();
      if (json?.errors?.length) {
        // Retry without metafield if scope/visibility blocks the query
        return await this._fetchCollectionTitlesBasic(uniqueHandles, {
          token,
          endpoint,
          LANG,
          COUNTRY,
          collectionsBase,
          signal,
        });
      }

      const map = {};
      uniqueHandles.forEach((handle, i) => {
        const node = json?.data?.[`c${i}`];
        if (!node?.title) return;
        map[handle] = {
          title: node.title,
          url: `${collectionsBase}/${encodeURIComponent(node.handle || handle)}`,
          isMatch: node.collectionType?.value === 'match' || handle.endsWith('-match'),
        };
      });

      return map;
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      this._debugSet({
        phase: 'collections-error',
        name: error?.name,
        message: error?.message,
        lang: LANG,
        country: COUNTRY,
      });
      return {};
    }
  }

  /** Fallback if metafield is not storefront-accessible — title + handle only. */
  async _fetchCollectionTitlesBasic(handles, { token, endpoint, LANG, COUNTRY, collectionsBase, signal = this.abortController.signal }) {
    const variables = { lang: LANG, country: COUNTRY };
    const fields = handles
      .map((handle, i) => {
        variables[`handle${i}`] = handle;
        return `c${i}: collection(handle: $handle${i}) { title handle }`;
      })
      .join('\n');

    const varDefs = [
      '$lang: LanguageCode!',
      '$country: CountryCode!',
      ...handles.map((_, i) => `$handle${i}: String!`),
    ].join(', ');

    const query = `
      query GetAutocompleteCollectionTitlesBasic(${varDefs})
      @inContext(language: $lang, country: $country) {
        ${fields}
      }
    `;

    const res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({ query, variables }),
      signal,
    });

    if (!res.ok) return {};
    const json = await res.json();
    if (json?.errors?.length) {
      this._debugSet({
        phase: 'collections-graphql-error',
        errors: json.errors,
        lang: LANG,
        country: COUNTRY,
        endpoint,
      });
      return {};
    }

    const map = {};
    handles.forEach((handle, i) => {
      const node = json?.data?.[`c${i}`];
      if (!node?.title) return;
      map[handle] = {
        title: node.title,
        url: `${collectionsBase}/${encodeURIComponent(node.handle || handle)}`,
        isMatch: handle.endsWith('-match'),
      };
    });
    return map;
  }

  _renderResults(result) {
    const sections = result?.sections ?? {};
    const suggestions = sections['Search Suggestions'] ?? [];
    // Prefer Categories; fall back to Collections if the index uses that section name
    const rawCategories = (sections['Categories'] ?? sections['Collections'] ?? []).filter(
      (item) => item?.data?.handle
    );
    const localizedCollections = result._localizedCollections || {};
    // Prefer non-match (same idea as Shopify predictive-search). If only match remain, show them.
    const nonMatchCategories = rawCategories.filter((item) => {
      const handle = item.data.handle;
      const localized = localizedCollections[handle];
      if (localized) return !localized.isMatch;
      return !handle.endsWith('-match');
    });
    const categories = nonMatchCategories.length > 0 ? nonMatchCategories : rawCategories;
    const productItemsHtml = result._productItemsHtml || '';
    const productNodes = productItemsHtml
      ? Array.from(new DOMParser().parseFromString(productItemsHtml, 'text/html').querySelectorAll('.js-fetched-item'))
      : [];
    const renderedProductCount = productNodes.length;
    const totalResults = suggestions.length + categories.length + renderedProductCount;
    const hasProducts = renderedProductCount > 0;

    this._debugSet({
      phase: 'render',
      searchTerm: this.searchTerm,
      localizedCollections,
      counts: {
        suggestions: suggestions.length,
        categoriesShown: categories.length,
        products: renderedProductCount,
      },
    });

    this.predictiveSearchResults.innerHTML = '';
    this._mobileTabsInitialised = false;

    if (totalResults === 0) {
      this.showEmpty();
      return;
    }

    const wrapperTpl = document.getElementById('constructor-predictive-search-results');
    if (!wrapperTpl) return;

    const fragment = wrapperTpl.content.cloneNode(true);
    const groupsEl = fragment.querySelector('[data-predictive-search-groups]');
    const countEl = fragment.querySelector('[data-predictive-search-live-region-count-value]');

    if (groupsEl) {
      const suggestionsFrag = this._buildSuggestionsHTML(suggestions, categories, localizedCollections, {
        hasProducts,
      });
      if (suggestionsFrag) {
        groupsEl.appendChild(suggestionsFrag);
      } else {
        groupsEl.classList.add('predictive-search__results-groups-wrapper--no-suggestions');
      }

      if (hasProducts) {
        const productsFrag = this._buildProductsHTML(productItemsHtml, renderedProductCount, productNodes);
        if (productsFrag) groupsEl.appendChild(productsFrag);
      }
    }

    if (countEl) countEl.textContent = String(totalResults);

    this.predictiveSearchResults.appendChild(fragment);
    this.setAttribute('results', '');
    this.open();
    this.removeAttribute('loading');
    window.SearchPopup?.bindPhraseChips?.(this.predictiveSearchResults);

    if (this.statusElement) {
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = String(totalResults);
      setTimeout(() => this.statusElement.setAttribute('aria-hidden', 'true'), 1000);
    }

    this._highlightSearchedWord();
    window.SearchPopup?.dedupeMobileProductTabs?.(this.predictiveSearchResults);
    this._mobileTabs();
  }

  /**
   * Idle Search popup settings (Most searched + Favorite categories)
   * when Constructor returns no phrases/collections.
   */
  _buildSidebarFallbackHTML(hasProducts = false) {
    const fallbackTpl = document.getElementById('search-popup-sidebar-fallback');
    if (!fallbackTpl?.content) return null;

    const frag = fallbackTpl.content.cloneNode(true);
    const aside = frag.querySelector('[data-search-sidebar-fallback]');
    if (!aside) return null;
    if (!aside.querySelector('.search-popup__section--phrases, .search-popup__section--categories')) {
      return null;
    }

    const tabProducts = frag.querySelector('[data-tab-products]');
    const tabCategories = frag.querySelector('[data-tab-categories]');
    const hasCategories = Boolean(aside.querySelector('.search-popup__section--categories'));

    if (tabProducts) {
      if (hasProducts) {
        tabProducts.hidden = false;
        this._tabProducts = tabProducts;
      } else {
        tabProducts.remove();
      }
    }

    if (tabCategories) {
      if (hasCategories) {
        tabCategories.hidden = false;
      } else {
        tabCategories.remove();
      }
    }

    return frag;
  }

  _buildSuggestionsHTML(suggestions, categories = [], localizedCollections = {}, { hasProducts = false } = {}) {
    if (!suggestions.length && !categories.length) {
      return this._buildSidebarFallbackHTML(hasProducts);
    }

    const wrapperTpl = document.getElementById('constructor-predictive-search-suggestions');
    if (!wrapperTpl) return null;

    const wrapper = wrapperTpl.content.cloneNode(true);
    const suggestionsSlot = wrapper.querySelector('[data-predictive-search-suggestions-slot]');
    const categoriesSlot = wrapper.querySelector('[data-predictive-search-categories-slot]');
    const phraseItemTpl = document.getElementById('constructor-predictive-search-suggestion-item');
    const categoryItemTpl =
      document.getElementById('constructor-predictive-search-category-item') || phraseItemTpl;
    const tabProducts = wrapper.querySelector('[data-tab-products]');
    const tabCategories = wrapper.querySelector('[data-tab-categories]');
    const collectionsBase = (window.routes?.collections_url || '/collections').replace(/\/$/, '');
    if (tabProducts) this._tabProducts = tabProducts;

    if (suggestions.length > 0 && suggestionsSlot && phraseItemTpl) {
      const listTpl = document.getElementById('constructor-predictive-search-suggestions-list');
      const listClone = listTpl?.content.cloneNode(true);
      const listEl = listClone?.querySelector('[data-predictive-search-suggestion-list]');
      if (listEl) {
        suggestions.forEach((item, index) => {
          const itemFrag = phraseItemTpl.content.cloneNode(true);
          const li = itemFrag.querySelector('[data-predictive-search-suggestion]');
          if (li) li.id = `predictive-search-option-suggestion-${index + 1}`;
          const link = itemFrag.querySelector('[data-predictive-search-suggestion-link]');
          if (link) {
            link.setAttribute('data-search-phrase-value', item.value);
            if (link.hasAttribute('data-predictive-search-suggestion-label')) {
              link.textContent = item.value;
            }
          }
          const label = itemFrag.querySelector('[data-predictive-search-suggestion-label]');
          if (label && label !== link) label.textContent = item.value;
          listEl.appendChild(itemFrag);
        });
        suggestionsSlot.replaceWith(listClone);
      } else {
        suggestionsSlot.remove();
      }
    } else if (suggestionsSlot) {
      suggestionsSlot.remove();
    }

    if (categories.length > 0 && categoriesSlot && categoryItemTpl) {
      const listTpl = document.getElementById('constructor-predictive-search-categories-list');
      const listClone = listTpl?.content.cloneNode(true);
      const listEl = listClone?.querySelector('[data-predictive-search-category-list]');
      const countEl = listClone?.querySelector('[data-predictive-search-categories-count]');
      const showAll = listClone?.querySelector('[data-predictive-search-categories-show-all]');
      if (listEl) {
        const visibleCategories = categories.slice(0, 5);
        visibleCategories.forEach((item, index) => {
          const handle = item.data?.handle;
          if (!handle) return;
          const localized = localizedCollections[handle];
          const title = localized?.title || item.value || handle;
          const href = localized?.url || `${collectionsBase}/${encodeURIComponent(handle)}`;
          const itemFrag = categoryItemTpl.content.cloneNode(true);
          const li = itemFrag.querySelector('[data-predictive-search-suggestion]');
          if (li) {
            li.id = `predictive-search-option-collection-${index + 1}`;
            li.setAttribute('data-cnstrc-item-section', 'Categories');
            li.setAttribute('data-cnstrc-item-name', title);
          }
          const link = itemFrag.querySelector('[data-predictive-search-suggestion-link]');
          if (link) link.setAttribute('href', href);
          const label = itemFrag.querySelector('[data-predictive-search-suggestion-label]');
          if (label) label.textContent = title;
          listEl.appendChild(itemFrag);
        });
        if (countEl) countEl.textContent = `(${categories.length})`;
        if (showAll && categories.length > 5) {
          showAll.hidden = false;
          const searchUrl = window.routes?.search_url || '/search';
          showAll.setAttribute('href', `${searchUrl}?q=${encodeURIComponent(this.searchTerm)}`);
        }
        categoriesSlot.replaceWith(listClone);
        if (tabCategories) {
          tabCategories.hidden = false;
          tabCategories.innerHTML = `${tabCategories.textContent.trim()} <span>(${categories.length})</span>`;
        }
      } else {
        categoriesSlot.remove();
      }
    } else if (categoriesSlot) {
      categoriesSlot.remove();
    }

    return wrapper;
  }

  _buildProductsHTML(productItemsHtml, productCount = 0, productNodes = null) {
    const tpl = document.getElementById('constructor-predictive-search-products');
    if (!tpl) return null;

    const fragment = tpl.content.cloneNode(true);
    const listEl = fragment.querySelector('[data-predictive-search-products-list]');
    const countEl = fragment.querySelector('[data-predictive-search-products-count]');
    const buttonSlot = fragment.querySelector('[data-predictive-search-button-slot]');

    if (countEl) countEl.textContent = `(${productCount})`;

    fragment.querySelector('#predictive-search-products')?.setAttribute('hidden', '');

    if (listEl) {
      const nodes =
        productNodes ||
        Array.from(new DOMParser().parseFromString(productItemsHtml, 'text/html').querySelectorAll('.js-fetched-item'));
      nodes.forEach((li, index) => {
        li.id = `predictive-search-option-product-${index + 1}`;
        li.classList.add('predictive-search__list-item', 'product-grid');
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', 'false');
        listEl.appendChild(li);
      });
    }

    if (buttonSlot) {
      const btnFrag = this._buildSearchForButtonHTML(productCount);
      if (btnFrag) {
        // Keep the .search-popup__cta wrapper so centering styles apply
        buttonSlot.replaceChildren(btnFrag);
      } else {
        buttonSlot.remove();
      }
    }

    if (this._tabProducts && productCount > 0) {
      const baseLabel = this._tabProducts.textContent.replace(/\s*\(\d+\)\s*$/, '').trim();
      this._tabProducts.hidden = false;
      this._tabProducts.classList.add('active');
      this._tabProducts.setAttribute('aria-selected', 'true');
      this._tabProducts.innerHTML = `${baseLabel} <span>(${productCount})</span>`;
    }

    return fragment;
  }

  _buildSearchForButtonHTML(productCount = 0) {
    const tpl = document.getElementById('constructor-predictive-search-search-for');
    if (!tpl) return null;
    const frag = tpl.content.cloneNode(true);
    const btn = frag.querySelector('[data-predictive-search-search-for-text]');
    if (btn && productCount > 0 && window.constructorStrings?.showAllResults) {
      btn.textContent = window.constructorStrings.showAllResults;
    }
    return frag;
  }

  showIdle() {
    this.removeAttribute('loading');
    this.removeAttribute('results');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'true');
    this.input.setAttribute('aria-activedescendant', '');
    this.pageBody.classList.add('is_fixed');
    window.SearchPopup?.showIdle(this);
    const idle = this.querySelector('[data-search-panel="idle"]');
    if (idle) idle.hidden = false;
  }

  showEmpty() {
    this.removeAttribute('loading');
    this.setAttribute('results', '');
    this.setAttribute('open', '');
    this.input.setAttribute('aria-expanded', 'true');
    this.pageBody.classList.add('is_fixed');
    window.SearchPopup?.showEmpty(this);
  }

  open() {
    this.predictiveSearchResults?.style.removeProperty('max-height');
    this.setAttribute('open', '');
    this.input.setAttribute('aria-expanded', 'true');
    this.pageBody.classList.add('is_fixed');
    window.SearchPopup?.showResults(this);
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    if (this.pageBody.classList.contains('is_fixed')) {
      this.pageBody.classList.remove('is_fixed');
    }
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      this._mobileTabsInitialised = false;
      this.searchTerm = '';
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', 'false');

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.predictiveSearchResults.removeAttribute('style');
    this.querySelectorAll('[data-search-panel]').forEach((panel) => {
      panel.hidden = true;
    });
  }

  _highlightSearchedWord() {
    const currval = this.input.value;
    const onlyLower = currval === currval.toLowerCase();
    const escaped = currval.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const filterItems = this.querySelectorAll('[data-highlight]');
    for (let i = 0; i < filterItems.length; i++) {
      const textcontent = filterItems[i].textContent;
      const finalval = textcontent.replace(new RegExp(`(${escaped})`, 'i'), (_, p1) => {
        return '<strong>' + (onlyLower ? p1 : currval) + '</strong>';
      });
      filterItems[i].innerHTML = finalval;
    }
  }

  saveLastSearched() {
    const value = this.input.value.trim();
    if (!value) return;

    let stored = [];
    try {
      stored = JSON.parse(localStorage.getItem('lastSearched') || '[]');
    } catch {}

    stored = stored.filter((v) => v !== value);
    stored.push(value);
    if (stored.length > 5) stored = stored.slice(stored.length - 5);
    localStorage.setItem('lastSearched', JSON.stringify(stored));
  }

  _mobileTabs() {
    window.SearchPopup?.bindMobileTabClicks?.(this);
    window.SearchPopup?.syncDefaultMobileTab?.(this);
    this._mobileTabsInitialised = true;
  }
}

customElements.define('constructor-autocomplete', ConstructorAutocomplete);
